import { atom } from "jotai";

/**
 * NOT a directory of brands. Holds at most the logged-in brand.
 *
 * This used to hold the entire brand table so dropdowns could filter it locally,
 * which silently hid every brand outside the fetched window. Brand pickers now
 * query the database (`useBrandSearch`) and labels resolve via `useBrandNames` /
 * `brandNameCacheAtom` — never from this atom.
 */
export const brandsAtom = atom<IBrand[]>([]);
export const brandsLoadingAtom = atom<boolean>(false);
export const loggedInBrandAtom = atom<IBrand | null>(null);
export const singleBrandAtom = atom<IBrand | null>(null);
export const singleBrandLoadingAtom = atom<boolean>(false);

export interface IBrand {
    id?: number;
    name: string;
    shopify_brand_name: string;
    top_up_amount?: number;
    waiting_payment?: boolean;
    static_header_rpe?: string;
    brand_logo?: string;
    rect_logo?: string;
    header_image?: string;
    channels?: IChannel[];
    use_expensive_box?: boolean;
    // shopify_api_key?: string;
    // shopify_api_secret?: string;
    // shopify_access_token?: string;
    production_enabled?: boolean;
    drop_pause_time?: number;
    design_title_prefix?: string | null;
    design_title_suffix?: string | null;
    /**
     * DB column on order.brands. Wrapping-paper roll lengths in feet. NULL means the
     * brand has not chosen and the server applies its defaults (10, 25, 40) — do not
     * treat NULL as "no lengths". 2-5 distinct values, each 10-100.
     */
    giftwrap_roll_lengths_ft?: number[] | null;
    /** Public-facing storefront domain customers visit, e.g. 'lumorawalls.com'. */
    storefront_domain?: string | null;
    available_to_designers?: boolean;
    /**
     * DB column on order.brands. When true the brand is permanently exempt from the
     * account-type pricing sync, independent of the manual exclusion list. Does not
     * affect Shopify-connect promotion.
     */
    pricing_protected?: boolean;
    /**
     * Derived read-only flag from the backend (NOT a column). True when this
     * brand is an active artist influencer. Gates the Artist Roster settings tab.
     */
    is_artist_influencer?: boolean;
    /** Derived read-only display value: the influencer's materials discount percent. */
    influencer_discount_percent?: number;
}

export interface IUploadLogoRequest {
    id?: number;
    name: string;
    shopify_brand_name: string;
    logo?: string;
    rect_logo?: string;
}

export interface IChannelPayload {
    store_name: string;
    store_domain: string;
    brand_id: number;
}

export interface IChannel {
    id?: number;
    created_at: string;
    enabled: boolean;
    connection_data: IShopifyConnectionData;
    channel_type: IChannelType;
    brand_id: number;
    channel_settings: IChannelSettings;
}

export interface IChannelSettings {
    is_order_source: boolean;
    is_product_creation_enabled: boolean;
}

export interface IShopifyConnectionData {
    shopify_brand_name: string;
    shopify_api_key: string;
    shopify_api_secret: string;
    shopify_access_token: string;
    shopify_client_id: string;
    shopify_client_secret: string;
    download_link?: string;
}

export enum IChannelType {
    SHOPIFY = 'SHOPIFY',
    WALLMATES = 'WALLMATES'
}

export interface IBrandFeatureFlag {
    id?: number;
    brand_id: number;
    flag_name: string;
    enabled: boolean;
}
// #region brand search
/**
 * id -> name cache for brands, populated on demand by `useBrandNames`.
 *
 * Brand pickers now search the database a page at a time, so a SELECTED brand is
 * usually NOT in the current result page. Trigger labels and filter badges must
 * therefore read from this cache, never from `options.find(...)`.
 */
export const brandNameCacheAtom = atom<Record<number, string>>({});

/** Lightweight dropdown row returned by `GET /brands/search`. */
export interface IBrandOption {
    id: number;
    name: string;
    active: boolean;
}

export interface IBrandSearchPage {
    brands: IBrandOption[];
    /** Total matching rows in the database, NOT `brands.length`. Drives "load more". */
    total: number;
    limit: number;
    offset: number;
}

export interface IBrandSearchQuery {
    q?: string;
    /** 'true' (default) = active only, 'false' = inactive only, 'all' = both. */
    active?: 'true' | 'false' | 'all';
    /** Include brands with a blank name — the Brands admin page needs these. */
    includeUnnamed?: boolean;
    /** Return whole brand rows instead of the lite id/name/active shape. */
    full?: boolean;
    /** Only brands with at least one order (Orders page filter). Inactive included unless `active` is set. */
    hasOrders?: boolean;
    limit?: number;
    offset?: number;
}
